import type { RouteRecordRaw } from 'vue-router'
import oidcAuth from '@/auth/oidcAuth'
import HomePage from '@/v2/pages/HomePage.vue'
import ClientsListPage from '@/v2/pages/ClientsListPage.vue'
import ContactsListPage from '@/v2/pages/ContactsListPage.vue'
import ExportsCegidAdminPage from '@/v2/pages/ExportsCegidAdminPage.vue'
import CommercialDashboardPage from '@/v2/pages/CommercialDashboardPage.vue'
import ReassignClientsAdminPage from '@/v2/pages/ReassignClientsAdminPage.vue'
import QuotesExportAdminPage from '@/v2/pages/QuotesExportAdminPage.vue'
import OnboardingListPage from '@/v2/pages/OnboardingListPage.vue'
import OnboardingCreatePage from '@/v2/pages/OnboardingCreatePage.vue'
import OnboardingDetailPage from '@/v2/pages/OnboardingDetailPage.vue'
import CollaboratorsListPage from '@/v2/pages/CollaboratorsListPage.vue'
import CollaboratorDetailPage from '@/v2/pages/CollaboratorDetailPage.vue'
import MyMaterialPage from '@/v2/pages/MyMaterialPage.vue'
import AccompagnementListPage from '@/v2/pages/AccompagnementListPage.vue'
import AccompagnementCreatePage from '@/v2/pages/AccompagnementCreatePage.vue'
import AccompagnementStatsPage from '@/v2/pages/AccompagnementStatsPage.vue'
import AccompagnementDetailPage from '@/v2/pages/AccompagnementDetailPage.vue'
import AccompagnementEditPage from '@/v2/pages/AccompagnementEditPage.vue'
import FactorDepositPage from '@/v2/pages/FactorDepositPage.vue'
import ActionsListPage from '@/v2/pages/ActionsListPage.vue'
import CronicleJobsPage from '@/v2/pages/CronicleJobsPage.vue'

/**
 * Routes V2 — chemins propres (pas de préfixe /v2/*) avec leurs propres composants
 * sous front/src/v2/. Toutes taggées `meta.version: 'v2'` :
 *   - le guard router redirige vers la home V1 si l'utilisateur est en mode v1
 *   - le guard router redirige vers la home V1 si la BU de l'user n'a pas activé le layout V2 (pref `layoutApy`)
 *   - App.vue sélectionne ApyLayout sur ces routes
 *
 * Convention : si une route V1 a un équivalent V2, choisir un path différent
 * (ex: /search V1 vs /recherche V2) — pas de partage de path entre versions.
 *
 * Note : /recherche est intentionnellement absent d'ici — c'est une route globale
 * gérée par le routeur principal, accessible en V1 et V2 via le flag OMNISEARCH.
 */
export const v2Routes: RouteRecordRaw[] = [
  {
    path: '/accueil',
    name: 'v2-home',
    component: HomePage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
    },
  },
  {
    path: '/mes-actions',
    name: 'v2-actions',
    component: ActionsListPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Mes actions',
    },
  },
  {
    path: '/clients',
    name: 'v2-clients',
    component: ClientsListPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Clients',
    },
  },
  {
    path: '/contacts',
    name: 'v2-contacts',
    component: ContactsListPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Contacts',
    },
  },
  {
    path: '/admin/exports-cegid',
    name: 'v2-admin-exports-cegid',
    component: ExportsCegidAdminPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Exports Cegid (TRA)',
      requiresSuperAdmin: true,
    }
  },
  {
    path: '/admin/reassign-clients',
    name: 'v2-admin-reassign-clients',
    component: ReassignClientsAdminPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Réassignation clients',
      requiresSuperAdmin: true,
    },
  },
  {
    path: '/admin/exports-devis',
    name: 'v2-admin-exports-devis',
    component: QuotesExportAdminPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Export devis (lignes)',
      requiresSuperAdmin: true,
    },
  },
  {
    path: '/commercial/dashboard',
    name: 'commercial-dashboard',
    component: CommercialDashboardPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'global',
      requiresSalesRead: true,
    },
  },
  // Onboarding / Offboarding — Hub Interne, réservé DEV_ADMIN pour l'instant.
  {
    path: '/onboarding',
    name: 'v2-onboarding',
    component: OnboardingListPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Matériel collaborateur',
      requiresDevAdmin: true,
    },
  },
  {
    path: '/onboarding/new',
    name: 'v2-onboarding-new',
    component: OnboardingCreatePage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Nouvelle demande',
      requiresDevAdmin: true,
    },
  },
  // Vue collaborateur (inventaire consolidé) — AVANT `/onboarding/:id`.
  {
    path: '/onboarding/collaborateurs',
    name: 'v2-onboarding-collaborators',
    component: CollaboratorsListPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Collaborateurs',
      requiresDevAdmin: true,
    },
  },
  {
    path: '/onboarding/collaborateurs/:userUuid',
    name: 'v2-onboarding-collaborator-detail',
    component: CollaboratorDetailPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Fiche collaborateur',
      requiresDevAdmin: true,
    },
  },
  {
    path: '/onboarding/:id',
    name: 'v2-onboarding-detail',
    component: OnboardingDetailPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Dossier onboarding',
      requiresDevAdmin: true,
    },
  },
  // Self-service : « Mon matériel » — accessible à tout utilisateur authentifié
  // (lecture seule, aucune permission MATERIAL requise).
  {
    path: '/mon-materiel',
    name: 'v2-my-material',
    component: MyMaterialPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Mon matériel',
    },
  },
  // Demande d'accompagnement — Mon activité. Front V2 sur API mockée (localStorage).
  // `/accompagnement/stats` et `/nouvelle` AVANT `/:id` (sinon `:id` les capture).
  {
    path: '/accompagnement',
    name: 'v2-accompagnement-list',
    component: AccompagnementListPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: "Demandes d'accompagnement",
      requiresAccompRead: true,
    },
  },
  {
    path: '/accompagnement/nouvelle',
    name: 'v2-accompagnement-create',
    component: AccompagnementCreatePage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: "Nouvelle demande d'accompagnement",
      requiresAccompRead: true,
    },
  },
  {
    path: '/accompagnement/stats',
    name: 'v2-accompagnement-stats',
    component: AccompagnementStatsPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Accompagnement — statistiques',
      requiresAccompRead: true,
    },
  },
  {
    path: '/accompagnement/:id/editer',
    name: 'v2-accompagnement-edit',
    component: AccompagnementEditPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: "Éditer la demande d'accompagnement",
      requiresAccompRead: true,
    },
  },
  {
    path: '/accompagnement/:id',
    name: 'v2-accompagnement-detail',
    component: AccompagnementDetailPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: "Détail demande d'accompagnement",
      requiresAccompRead: true,
    },
  },
  // Dépôt BNP Factor (affacturage) — Mon activité, réservé DEV_ADMIN pour l'instant.
  {
    path: '/mon-activite/factor',
    name: 'v2-factor-deposit',
    component: FactorDepositPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Dépôt BNP Factor',
      requiresDevAdmin: true,
    },
  },
  // Supervision des jobs Cronicle — Hub Interne, réservé DEV_ADMIN.
  {
    path: '/admin/cronicle',
    name: 'v2-admin-cronicle',
    component: CronicleJobsPage,
    meta: {
      authName: oidcAuth.authName,
      hideRightSidebar: true,
      version: 'v2',
      title: 'Supervision des jobs',
      requiresDevAdmin: true,
    },
  },
]
